import {
  Accordion,
  AccordionItem,
  AccordionItemButton,
  AccordionItemHeading,
  AccordionItemPanel,
} from 'react-accessible-accordion'
import {
  activeSubscriptionSelector,
  loggedInSelector,
} from 'spa/selectors/users'
import { DynamicMessage } from '../../components/message'
import {
  faqMessages,
  featuresMessages,
  logosMessages,
  messages,
} from './localizedMessages'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Helmet } from 'react-helmet'
import { injectIntl } from 'react-intl'
import { openModal, SIGNUP_MODAL } from 'spa/action_creators/modals'
import { TextStyle } from 'spa/components/typography'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import PropTypes from 'prop-types'
import React, { useState } from 'react'
import styles from './styles'

const faqStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name:
        'Why Is DJcity the Best DJ Record Pool for Serato, Rekordbox, Traktor, and VirtualDJ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'DJcity offers high-quality 320kbps MP3 downloads, ensuring top-tier sound. Every track is pre-filled with metadata for effortless organization.',
      },
    },
    {
      '@type': 'Question',
      name: 'How Do I Import DJcity Downloads into My DJ Software?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'Simply drag and drop your downloaded tracks into your preferred DJ software. Your tracks are ready to mix in seconds.',
      },
    },
    {
      '@type': 'Question',
      name: 'What Type of Music and Genres Can I Find on DJcity?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'DJcity offers a diverse selection of over 250 genres plus exclusive remixes and edits, catering to any event or audience.',
      },
    },
    {
      '@type': 'Question',
      name: 'Why Do DJs Prefer DJcity Over Other DJ Record Pools?',
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          'DJs worldwide trust DJcity for its expert-curated playlists, broad genre selection, and seamless compatibility with all major DJ software.',
      },
    },
  ],
}

export const IntegrationsLogos = injectIntl(({ intl }) => {
  return (
    <div className={styles.logos}>
      {Object.entries(logosMessages).map(([key, value]) => (
        <img
          alt={'logo_' + key}
          className={styles.logo}
          id={'logo_' + key}
          key={'logo_' + key}
          src={intl.formatMessage(value)}
        />
      ))}
    </div>
  )
})

const Integrations = ({ intl }) => {
  const isLoggedIn = useSelector(loggedInSelector)
  const isActive = useSelector(activeSubscriptionSelector)

  const dispatch = useDispatch()
  const history = useHistory()

  const [isOpen, setOpen] = useState(null)

  const handleFeature = () => history.push('/home')

  const handleClick = () => {
    if (isLoggedIn) {
      if (!isActive) history.push('/payment-details')
      else history.push('/home') // make invisible if active
    } else {
      dispatch(openModal(SIGNUP_MODAL))
    }
  }

  const handleFaq = ([uuid]) =>
    uuid === isOpen ? setOpen(null) : setOpen(uuid)

  return (
    <main className={styles.page}>
      <Helmet
        defer={false}
        htmlAttributes={{
          itemScope: undefined,
          itemType: 'https://schema.org/FAQPage',
        }}
      >
        <title key="pagetitle">
          {`${intl.formatMessage(messages.pageTitle)} | DJcity`}
        </title>
        <meta
          content={intl.formatMessage(messages.pageDescription)}
          name="description"
        ></meta>
        <script type="application/ld+json">
          {JSON.stringify(faqStructuredData)}
        </script>
      </Helmet>
      <section className={styles.softwares}>
        <h1 aria-level={1}>{intl.formatMessage(messages.title)}</h1>
        <TextStyle color="white">
          {intl.formatMessage(messages.subtitle)}
        </TextStyle>

        <IntegrationsLogos />

        <div className={styles.features}>
          {Object.values(featuresMessages).map((feat, idx) => {
            return (
              <div
                className={styles['feature']}
                key={'integrations_feat_' + idx}
                onClick={handleFeature}
              >
                <img
                  className={styles.featureImage}
                  src={intl.formatMessage(feat.image)}
                />
                <div className={styles.featureText}>
                  <h2 aria-level={2}>{intl.formatMessage(feat.title)}</h2>
                  <span>{intl.formatMessage(feat.subTitle)}</span>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      <section className={styles.c2a}>
        <span>{intl.formatMessage(messages.c2aTitle)}</span>
        <button onClick={handleClick}>
          {intl.formatMessage(messages.c2aButton)}
        </button>
      </section>

      <section className={styles.faqs}>
        <h2>{intl.formatMessage(messages.faqTitle)}</h2>
        <Accordion
          // allowMultipleExpanded
          className={styles.accordion}
          onChange={handleFaq}
        >
          {faqMessages.map((faq, idx) => (
            <AccordionItem
              className={styles.accordionItem}
              dangerouslySetExpanded={isOpen === faq.uuid}
              itemprop="mainEntity"
              itemscope
              itemtype="https://schema.org/Question"
              key={'integrations_faq_' + idx}
              uuid={faq.uuid}
            >
              <AccordionItemHeading className={styles.accordionHeading}>
                <AccordionItemButton
                  className={styles.accordionButton}
                  itemprop="name"
                >
                  <DynamicMessage
                    aria-level={3}
                    defaultMessage={faq.msg.title.defaultMessage}
                    id={faq.msg.title.id}
                    tag="h3"
                  />
                  <FontAwesomeIcon
                    color={isOpen === faq.uuid ? 'red' : 'black'}
                    icon={`chevron-${isOpen === faq.uuid ? 'down' : 'right'}`}
                  />
                </AccordionItemButton>
              </AccordionItemHeading>
              <AccordionItemPanel
                className={styles.accordionPanel}
                itemprop="acceptedAnswer"
                itemscope
                itemtype="https://schema.org/Answer"
              >
                <DynamicMessage
                  defaultMessage={faq.msg.content.defaultMessage}
                  id={faq.msg.content.id}
                  itemprop="text"
                />
              </AccordionItemPanel>
            </AccordionItem>
          ))}
        </Accordion>
      </section>
    </main>
  )
}

Integrations.propTypes = {
  intl: PropTypes.object,
}

export default injectIntl(Integrations)
